import { useEffect, useState } from "react";
import { api, STATUSES, statusMeta } from "@/lib/api";
import { Plus, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import LeadFormDialog from "@/components/LeadFormDialog";
import { toast } from "sonner";

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [dragged, setDragged] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/leads"), api.get("/campaigns")]);
    setLeads(l.data);
    setCampaigns(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    try {
      await api.post("/leads", payload);
      toast.success("Lead created");
      setOpen(false);
      await load();
    } catch { toast.error("Failed"); }
  };

  const onDragStart = (id) => setDragged(id);
  const onDrop = async (status) => {
    if (!dragged) return;
    const lead = leads.find((l) => l.id === dragged);
    setDragged(null);
    if (!lead || lead.status === status) return;
    // Optimistic
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try {
      await api.put(`/leads/${lead.id}`, { status });
      toast.success(`Moved to ${statusMeta(status).label}`);
    } catch {
      toast.error("Move failed");
      await load();
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Kanban</div>
          <h1 className="font-heading text-4xl font-bold">Pipeline</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="pipeline-new-lead-btn">
          <Plus className="w-4 h-4 mr-1"/> New lead
        </Button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-6" data-testid="pipeline-kanban">
        {STATUSES.map((s) => {
          const items = leads.filter((l) => l.status === s.key);
          const total = items.reduce((sum, l) => sum + (l.estimated_value || 0), 0);
          return (
            <div
              key={s.key}
              className="kanban-col bg-zinc-100/60 border border-zinc-200 rounded-xl p-3 flex-shrink-0"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.key)}
              data-testid={`pipeline-col-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${s.color.split(" ")[0]}`}/>
                  <span className="font-heading font-bold text-sm">{s.label}</span>
                  <span className="font-mono-accent text-xs text-zinc-500">{items.length}</span>
                </div>
                <span className="font-mono-accent text-xs text-zinc-500">${total.toLocaleString()}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {items.map((l) => (
                  <div
                    key={l.id}
                    draggable
                    onDragStart={() => onDragStart(l.id)}
                    onClick={() => nav(`/app/leads/${l.id}`)}
                    className="bg-white border border-zinc-200 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:-translate-y-0.5 transition-transform card-crisp"
                    data-testid={`pipeline-card-${l.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{l.customer_name}</div>
                        <div className="text-xs text-zinc-500 truncate">{l.service_requested || "No service"}</div>
                      </div>
                      <GripVertical className="w-3.5 h-3.5 text-zinc-300 shrink-0"/>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono-accent text-xs text-zinc-600">${(l.estimated_value||0).toLocaleString()}</span>
                      {l.ai_score != null && (
                        <span className="font-mono-accent text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-[#E04F00] border border-orange-200">
                          AI {l.ai_score}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="text-center text-xs text-zinc-400 py-4">Drop here</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <LeadFormDialog open={open} onOpenChange={setOpen} onSubmit={create} campaigns={campaigns} />
    </div>
  );
}
